import { useEffect, useMemo, useRef, useState } from 'react';
import { apiClient, axiosClient } from '../../auth-lib';
import { useNavigate } from 'react-router-dom';
import EmployeeLayout from '../components/EmployeeLayout';
import { Button, Card, Input, Select, useToast } from '../../design-system';
import '../styles/CreateTask.css';

// Same title-case formatter used on the Services page — the catalog is
// SCREAMING_CASE throughout, so it needs the same acronym-aware cleanup
// here or every option in this dropdown would just shout.
const SMALL_WORDS = new Set(['of', 'in', 'by', 'to', 'and', 'on', 'at', 'for', 'the', 'a', 'an', 'or', 'from', 'with', 'within']);
const KEEP_UPPER = new Set([
  'tds', 'pan', 'tan', 'itr', 'roc', 'llp', 'din', 'fema', 'lodr', 'nclt', 'aoa', 'moa', 'rd',
  'iec', 'pt', 'ec', 'gst', 'gstr', 'cit', 'itat', 'fc', 'gpr', 'tc', 'trs', 'fla', 'odi',
  'ptec', 'ptrc', 'lut', 'sez', 'reg', 'cr', 'ii', 'i',
]);
const titleCaseWord = (word, isFirst) => {
  const lower = word.toLowerCase();
  const bare = lower.replace(/[^a-z]/g, '');
  if (KEEP_UPPER.has(bare)) return word.toUpperCase();
  if (!isFirst && SMALL_WORDS.has(lower)) return lower;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};
const formatServiceName = (raw) =>
  raw
    .split(' ')
    .map((token, idx) => {
      if (/[\d()@./]/.test(token)) return token;
      if (token.includes('-')) return token.split('-').map((p) => titleCaseWord(p, false)).join('-');
      return titleCaseWord(token, idx === 0);
    })
    .join(' ');

function CreateTask({ Layout = EmployeeLayout, backPath = '/employee/tasks' }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    tags: [],
    clientIds: [],
    preparerId: '',
    preparerHours: '',
    reviewerId: '',
    reviewerHours: '',
    assistantIds: [],
    assistantHours: {},
    isBillable: false,
    billType: 'professional_fees',
    billAmount: '',
    // Which catalog service this task is work against. Picking one used to
    // change nothing but the title text; it's now stored on the task.
    selectedServiceId: '',
  });
  const [orgUsers, setOrgUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [services, setServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [servicePickerOpen, setServicePickerOpen] = useState(false);
  // Two precise refs (the input itself and the results box) rather than
  // their wrapping <div> — that wrapper is a normal block element and
  // spans the full form width even though the input only renders at
  // 340px, so clicking the blank space next to the input was being
  // counted as "inside" and failing to close the picker.
  const serviceInputRef = useRef(null);
  const serviceBoxRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    axiosClient.get('/users').then((res) => setOrgUsers(res.data)).catch(() => {});
    apiClient.get('/api/services').then((res) => setServices(res.data)).catch(() => {});
    apiClient.get('/api/clients').then((res) => setClients(res.data)).catch(() => {});
  }, []);

  // Standard combobox behavior: open on focus, close on an outside click.
  useEffect(() => {
    if (!servicePickerOpen) return undefined;
    const handleClickOutside = (e) => {
      const insideInput = serviceInputRef.current && serviceInputRef.current.contains(e.target);
      const insideBox = serviceBoxRef.current && serviceBoxRef.current.contains(e.target);
      if (!insideInput && !insideBox) {
        setServicePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [servicePickerOpen]);

  // Grouped by category so the picker reads as category > sub-category,
  // matching how the Services page and the client Enroll form group them.
  // A plain scrollable box (not a native <select>) on purpose — a native
  // select's dropdown popup is positioned by the browser itself, which
  // was shifting the whole page to fit this list's 80+ options.
  const serviceGroups = useMemo(() => {
    const term = serviceSearch.trim().toLowerCase();
    const enabledServices = services.filter((s) => s.isEnabled !== false);
    const filtered = term
      ? enabledServices.filter(
          (s) => s.name.toLowerCase().includes(term) || s.groupName.toLowerCase().includes(term)
        )
      : enabledServices;

    const byGroup = new Map();
    filtered.forEach((s) => {
      const key = s.groupName.trim().toLowerCase();
      if (!byGroup.has(key)) byGroup.set(key, { displayName: s.groupName, items: [] });
      byGroup.get(key).items.push(s);
    });
    return Array.from(byGroup.values()).sort((a, b) => b.items.length - a.items.length);
  }, [services, serviceSearch]);

  const handleServiceSelect = (service) => {
    setFormData((prev) => ({
      ...prev,
      title: formatServiceName(service.name),
      selectedServiceId: service._id,
    }));
    setServicePickerOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBillableToggle = (e) => {
    setFormData((prev) => ({ ...prev, isBillable: e.target.checked }));
  };

  const toggleClient = (clientId) => {
    setFormData((prev) => ({
      ...prev,
      clientIds: prev.clientIds.includes(clientId)
        ? prev.clientIds.filter((existingId) => existingId !== clientId)
        : [...prev.clientIds, clientId],
    }));
  };

  // Clients matching the search box (name or code). Selected ones always show,
  // even when they don't match the current filter, so a selection can't hide.
  const visibleClients = useMemo(() => {
    const term = clientSearch.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter(
      (c) =>
        formData.clientIds.includes(c._id) ||
        c.displayName.toLowerCase().includes(term) ||
        (c.clientCode || '').toLowerCase().includes(term)
    );
  }, [clients, clientSearch, formData.clientIds]);

  const toggleAssistant = (userId) => {
    setFormData((prev) => ({
      ...prev,
      assistantIds: prev.assistantIds.includes(userId)
        ? prev.assistantIds.filter((existingId) => existingId !== userId)
        : [...prev.assistantIds, userId],
    }));
  };

  const setAssistantHours = (userId, hours) => {
    setFormData((prev) => ({
      ...prev,
      assistantHours: { ...prev.assistantHours, [userId]: hours },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Task title is required');
      return;
    }

    setLoading(true);
    try {
      const hourAllocations = [];
      if (formData.preparerId && formData.preparerHours) {
        hourAllocations.push({
          userId: formData.preparerId,
          role: 'preparer',
          allocatedHours: Number(formData.preparerHours),
        });
      }
      if (formData.reviewerId && formData.reviewerHours) {
        hourAllocations.push({
          userId: formData.reviewerId,
          role: 'reviewer',
          allocatedHours: Number(formData.reviewerHours),
        });
      }
      formData.assistantIds.forEach((userId) => {
        const hours = formData.assistantHours[userId];
        if (hours) {
          hourAllocations.push({ userId, role: 'assistant', allocatedHours: Number(hours) });
        }
      });

      const payload = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        dueDate: formData.dueDate,
        tags: formData.tags,
        clientIds: formData.clientIds,
        serviceId: formData.selectedServiceId || undefined,
        preparerId: formData.preparerId || undefined,
        reviewerId: formData.reviewerId || undefined,
        assistantIds: formData.assistantIds,
        hourAllocations,
        isBillable: formData.isBillable,
        billType: formData.isBillable ? formData.billType : undefined,
        billAmount: formData.isBillable && formData.billAmount ? Number(formData.billAmount) : undefined,
      };

      const response = await apiClient.post('/api/tasks', payload);
      toast.success('Task created');
      navigate(`${backPath}/${response.data._id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Create New Task" subtitle="Add a new task to your workflow">
      <Card className="create-task-card">
        <form onSubmit={handleSubmit} className="create-task-form">
          {error && <div className="form-error" role="alert">{error}</div>}

          <div className="create-task-grid">
            <div className="create-task-main">
              <Input
                label="Task Title *"
                id="title"
                name="title"
                placeholder="Enter task title"
                value={formData.title}
                onChange={handleChange}
                required
                disabled={loading}
              />

              {services.length > 0 && (
                <div className="service-picker-field">
                  <label className="ds-field-label">Or pick from Services (optional)</label>
                  <Input
                    ref={serviceInputRef}
                    className="service-picker-search"
                    placeholder="🔎 Search services…"
                    value={serviceSearch}
                    onChange={(e) => setServiceSearch(e.target.value)}
                    onFocus={() => setServicePickerOpen(true)}
                    disabled={loading}
                  />
                  {servicePickerOpen && (
                    <div className="service-picker-box" ref={serviceBoxRef}>
                      {serviceGroups.length === 0 ? (
                        <p className="empty-message">No services found</p>
                      ) : (
                        serviceGroups.map(({ displayName, items }) => (
                          <div key={displayName} className="service-picker-group">
                            <div className="service-picker-group-title">
                              {displayName} <span className="service-picker-group-count">({items.length})</span>
                            </div>
                            {items.map((s) => (
                              <button
                                key={s._id}
                                type="button"
                                className="service-picker-option"
                                onClick={() => handleServiceSelect(s)}
                                disabled={loading}
                              >
                                {formatServiceName(s.name)}
                              </button>
                            ))}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="ds-field">
                <label className="ds-field-label">Client(s) (optional) — link this task to one or more clients</label>
                <Input
                  className="service-picker-search"
                  placeholder="🔎 Search clients…"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  disabled={loading}
                />
                <div className="assistants-checkbox-list">
                  {clients.length === 0 ? (
                    <p className="empty-message">No clients yet</p>
                  ) : visibleClients.length === 0 ? (
                    <p className="empty-message">No clients match “{clientSearch}”</p>
                  ) : (
                    visibleClients.map((c) => (
                      <label key={c._id} className="assistant-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.clientIds.includes(c._id)}
                          onChange={() => toggleClient(c._id)}
                          disabled={loading}
                        />
                        {c.displayName}{c.clientCode ? ` (${c.clientCode})` : ''}
                      </label>
                    ))
                  )}
                </div>
                {formData.clientIds.length > 0 && (
                  <span className="service-picker-count">
                    {formData.clientIds.length} client{formData.clientIds.length === 1 ? '' : 's'} selected
                  </span>
                )}
              </div>

              <div className="ds-field">
                <label htmlFor="description" className="ds-field-label">Description</label>
                <textarea
                  id="description"
                  name="description"
                  className="ds-input"
                  placeholder="Enter task description"
                  rows="5"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="assistants-field">
                <label className="ds-field-label">
                  Assistants (optional) — juniors shadowing the preparer to help and learn. Set an hour
                  budget per person if you want to compare it against their actual logged hours later.
                </label>
                <div className="assistants-checkbox-list">
                  {orgUsers.map((u) => {
                    const checked = formData.assistantIds.includes(u._id);
                    return (
                      <div key={u._id} className="assistant-row">
                        <label className="assistant-checkbox">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAssistant(u._id)}
                            disabled={loading}
                          />
                          {u.name}
                        </label>
                        {checked && (
                          <input
                            type="number"
                            className="ds-input assistant-hours-input"
                            min="0"
                            step="0.5"
                            placeholder="Hours"
                            value={formData.assistantHours[u._id] || ''}
                            onChange={(e) => setAssistantHours(u._id, e.target.value)}
                            disabled={loading}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="billing-field">
                <label className="billable-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.isBillable}
                    onChange={handleBillableToggle}
                    disabled={loading}
                  />
                  Billable
                </label>
                {formData.isBillable && (
                  <div className="form-row">
                    <Select
                      label="Bill Type"
                      id="billType"
                      name="billType"
                      value={formData.billType}
                      onChange={handleChange}
                      disabled={loading}
                    >
                      <option value="professional_fees">Professional Fees</option>
                      <option value="ope">Out-of-Pocket Expenses (OPE)</option>
                    </Select>
                    <Input
                      label="Amount (₹)"
                      type="number"
                      id="billAmount"
                      name="billAmount"
                      min="0"
                      step="0.01"
                      placeholder="Enter amount"
                      value={formData.billAmount}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="create-task-sidebar">
              <div className="form-row">
                <Select
                  label="Priority"
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </Select>

                <Input
                  label="Due Date"
                  id="dueDate"
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              {/* No separate "Assigned To" picker — the Preparer below IS the
                  task's owner. See src/utils/taskPeople.js for why the two
                  fields were collapsed into one. */}
              <div className="assignee-row">
                <Select
                  label="Preparer"
                  id="preparerId"
                  name="preparerId"
                  value={formData.preparerId}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">-- None --</option>
                  {orgUsers.map((u) => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </Select>
                {formData.preparerId && (
                  <Input
                    label="Preparer Hours"
                    type="number"
                    name="preparerHours"
                    min="0"
                    step="0.5"
                    placeholder="Est. hours"
                    value={formData.preparerHours}
                    onChange={handleChange}
                    disabled={loading}
                  />
                )}
              </div>

              <div className="assignee-row">
                <Select
                  label="Reviewer"
                  id="reviewerId"
                  name="reviewerId"
                  value={formData.reviewerId}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">-- None --</option>
                  {orgUsers.map((u) => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </Select>
                {formData.reviewerId && (
                  <Input
                    label="Reviewer Hours"
                    type="number"
                    name="reviewerHours"
                    min="0"
                    step="0.5"
                    placeholder="Est. hours"
                    value={formData.reviewerHours}
                    onChange={handleChange}
                    disabled={loading}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <Button type="submit" variant="primary" loading={loading}>
              {loading ? 'Creating…' : 'Create Task'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate(backPath)}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </Layout>
  );
}

export default CreateTask;
